import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { ArrowUpRight, Search } from "lucide-react";
import PageIntro from "@/components/PageIntro";
import Reveal from "@/components/Reveal";
import { defaultExternalArticles } from "@/data/siteData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function normalizeExternalArticle(article) {
  return {
    id: article.id || article.url,
    title: article.title,
    platform: article.platform,
    url: article.url,
    publicationDate: article.publication_date || article.publicationDate,
    readingTime: article.reading_time || article.readingTime,
    excerpt: article.excerpt,
    topicTags: article.topic_tags || article.topicTags || [],
    ctaLabel: article.cta_label || article.ctaLabel || "Read Article",
    published: article.published !== false,
    order: article.order || 0,
  };
}

function articleKey(article) {
  return article.title.toLowerCase().replace(/[’']/g, "").replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "") || article.id;
}

function matchesArticle(article, query) {
  if (!query) return true;
  return [article.title, article.platform, article.excerpt, ...article.topicTags].join(" ").toLowerCase().includes(query);
}

function ExternalArticleCard({ article }) {
  const key = articleKey(article);
  return (
    <article className="external-article-card" data-testid={`article-card-${key}`}>
      <div className="external-article-meta">
        <span className="external-article-platform" data-testid={`article-platform-${key}`}>{article.platform}</span>
        <span data-testid={`article-date-${key}`}>{article.publicationDate} · {article.readingTime}</span>
      </div>
      <h3 data-testid={`article-title-${key}`}>{article.title}</h3>
      <p data-testid={`article-excerpt-${key}`}>{article.excerpt}</p>
      <div className="external-article-tags" data-testid={`article-tags-${key}`}>
        {article.topicTags.map((tag) => <span key={tag}>{tag}</span>)}
      </div>
      <a className="external-article-link" href={article.url} target="_blank" rel="noreferrer noopener" data-testid={`article-cta-${key}`} aria-label={`Read '${article.title}' on ${article.platform} (opens in a new tab)`}>
        {article.ctaLabel} <ArrowUpRight size={14} aria-hidden="true" />
      </a>
    </article>
  );
}

function FeaturedExternalArticle({ article }) {
  const key = articleKey(article);
  return (
    <article className="featured-external-article" data-testid="featured-article-card">
      <div className="featured-external-copy">
        <div className="external-article-meta">
          <span className="external-article-platform" data-testid={`article-platform-${key}`}>{article.platform}</span>
          <span data-testid={`article-date-${key}`}>{article.publicationDate} · {article.readingTime}</span>
        </div>
        <h2 data-testid={`article-title-${key}`}>{article.title}</h2>
        <p data-testid={`article-excerpt-${key}`}>{article.excerpt}</p>
        <div className="external-article-tags" data-testid={`article-tags-${key}`}>
          {article.topicTags.map((tag) => <span key={tag}>{tag}</span>)}
        </div>
        <a className="button-primary featured-external-link" href={article.url} target="_blank" rel="noreferrer noopener" data-testid={`article-cta-${key}`} aria-label={`Read '${article.title}' on ${article.platform} (opens in a new tab)`}>
          {article.ctaLabel} <ArrowUpRight size={15} aria-hidden="true" />
        </a>
      </div>
      <div className="featured-external-mark" aria-hidden="true"><span>01</span><strong>Published<br /><em>elsewhere.</em></strong></div>
    </article>
  );
}

export default function Articles() {
  const [articles, setArticles] = useState(defaultExternalArticles);
  const [platform, setPlatform] = useState("All Platforms");
  const [search, setSearch] = useState("");
  const [source, setSource] = useState("live");

  useEffect(() => {
    api.get(`${API}/external-articles`).then(({ data }) => setArticles(data.map(normalizeExternalArticle))).catch(() => setSource("sample"));
  }, []);

  const platforms = useMemo(() => ["All Platforms", ...new Set(articles.map((article) => article.platform))], [articles]);
  const visible = useMemo(() => {
    const query = search.trim().toLowerCase();
    return articles.filter((article) => (platform === "All Platforms" || article.platform === platform) && matchesArticle(article, query));
  }, [articles, platform, search]);
  const featured = visible[0];
  const countLabel = `${visible.length} ${visible.length === 1 ? "External Essay" : "External Essays"}`;

  return (
    <main data-testid="articles-page">
      <PageIntro index="03 / EXT" title="Articles" titleTestId="articles-page-title" descriptionTestId="articles-page-description" description="Long-form essays, external series, and practitioner commentaries published across Medium and the wider conversation." aside="This is where I keep the work that lives beyond this site: ideas tested in public, then brought back into the questions I keep exploring here." />
      <section className="page-section articles-page-section" style={{ paddingTop: 0 }}>
        <div className="shell">
          <Reveal>
            <div className="articles-toolbar" data-testid="articles-filter-bar">
              <div className="articles-platform-filters">
                {platforms.map((item) => <button type="button" key={item} className={`articles-filter-button ${platform === item ? "active" : ""}`} data-testid={`articles-filter-${item.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} onClick={() => setPlatform(item)}>{item}</button>)}
              </div>
              <label className="articles-search"><span className="sr-only">Search articles</span><Search size={15} aria-hidden="true" /><input type="search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search articles" data-testid="articles-search-input" /></label>
              <span className="articles-count" data-testid="articles-count-indicator">{countLabel}</span>
            </div>
          </Reveal>
          {featured ? <Reveal><FeaturedExternalArticle article={featured} /></Reveal> : <div className="articles-empty-state" data-testid="articles-empty-state"><strong>No articles match this view.</strong><span>Try another platform or search phrase.</span></div>}
          <div className="articles-grid" data-testid="articles-grid">
            {visible.slice(1).map((article) => <Reveal key={article.id}><ExternalArticleCard article={article} /></Reveal>)}
          </div>
          <p className="articles-source-note" data-testid="articles-source-note">{source === "live" ? "Managed through the private editorial workspace." : "The opening publication entry is shown while the editorial workspace reconnects."} <a href="/insights/manage" data-testid="articles-editor-link">Owner editor <ArrowUpRight size={13} aria-hidden="true" /></a></p>
        </div>
      </section>
    </main>
  );
}
